import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { API, PostDTO, PostFullDTO, TopicDTO, TopicFullDTO, UserDTO } from '../../../services/api/api';
import { SbDateTime } from '../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { DiscussionService } from '../../../services/discussion.service';
import { NavigationStart, Router } from '@angular/router';
import { ThemingService } from '@ngx-seatback/utils';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../../../services/util.service';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

@Component({
  selector: 'sb-discussion-component',
  templateUrl: './sb-discussion-component.component.html',
  styleUrls: ['./sb-discussion-component.component.scss'],
  standalone: false,
})
export class SbDiscussionComponentComponent implements OnInit {
  @ViewChild('discussionComponent', { static: false }) eRef: ElementRef;
  @ViewChild('textareaReply', { static: false }) textareaReply: ElementRef;
  replyClicked: boolean;
  currentUser: UserDTO;
  reply: string;
  fifthLevel: PostFullDTO[];
  solution: PostDTO | null;
  theme: string;
  mentionUsers: boolean;
  searchStatus: boolean;
  mentionSearchStr: string;
  mentions: UserDTO[];
  readOnly: boolean;
  timer: any;

  constructor(
    public api: API,
    public discussionService: DiscussionService,
    public router: Router,
    private theming: ThemingService,
    public translate: TranslateService,
    public util: UtilService,
    private cdRef: ChangeDetectorRef,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (this.discussionService.discussions) {
          this.discussionService.discussions.unsubscribe();
        }
        this.discussionService.discussions = null;
        this.discussionService.discussableElements = [];
        this.discussionService.groupedDiscussionList = [];
        this.discussionService.keyArr = [];
        this.discussionService.newTopic = false;
        this.discussionService.selectedTopic = null;
        this.discussionService.selectedGroup = null;
      }
    });

    this.solution = null;
    this.discussionService.selectedTopic = null;
    this.replyClicked = false;
    this.api
      .shared_User_retrieve()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe(data => {
        this.currentUser = data;
      });
    this.reply = '';
    this.searchStatus = false;
    this.mentions = [];
  }

  @HostListener('document:click', ['$event'])
  clickOutside(e: MouseEvent) {
    if (
      this.discussionService.showDiscussionSideBar &&
      this.discussionService.animationCompleted &&
      !this.eRef.nativeElement.contains(e.target) &&
      !this.discussionService.ribbonClicked &&
      !this.discussionService.modalOpen
    ) {
      this.discussionService.closeSideBar();
    }
  }

  ngOnInit(): void {
    this.theming.theme.subscribe((currentTheme: string) => {
      this.theme = currentTheme;
    });
  }

  lookForMentions($event: string) {
    if ($event.endsWith('@')) {
      this.searchStatus = true;
    }
    if (this.searchStatus) {
      const split = $event.split('@');
      const searchStr = split[split.length - 1];
      // TODO: check regex for mentions

      if (/@\w+.*/.test($event)) {
        this.mentionUsers = true;
        this.mentionSearchStr = searchStr;
      } else {
        this.mentionUsers = false;
        this.mentionSearchStr = '';
      }
    }
  }

  selectUser($event: any) {
    this.mentionUsers = false;
    if (!this.mentions) {
      this.mentions = [];
    }
    if (!this.mentions.find(x => x.id === $event.id)) {
      this.mentions.push($event);
    }
    this.reply = this.reply.replace('@' + this.mentionSearchStr, '');
    this.searchStatus = false;
    this.cdRef.detectChanges();
  }

  toggleReplyField($event: MouseEvent) {
    if ($event) {
      $event.stopPropagation();
    }
    this.replyClicked = !this.replyClicked;
  }

  sendReply($event: MouseEvent) {
    this.toggleReplyField($event);
    this.discussionService.sendReply(this.reply, this.discussionService.selectedTopic.id, true, this.mentions).then(() => {
      this.updateTopic().then();
      this.reply = '';
      this.mentions = [];
    });
  }

  toggleSideBar() {
    if (this.discussionService.showDiscussionSideBar) {
      this.discussionService.closeSideBar();
    } else {
      this.discussionService.openSideBar();
    }
  }

  selectTopic(element: any, topic: TopicDTO, $event: MouseEvent): Promise<boolean> {
    if ($event) {
      $event.stopPropagation();
    }
    return new Promise(resolve => {
      this.discussionService.selectDiscussableGroup(element.id);
      // event in chat for subtitle
      this.api
        .shared_Discussion_retrieveTopicById(topic?.id)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe((mainPost: TopicFullDTO) => {
          this.discussionService.selectedTopic = mainPost;
          resolve(true);
        });
    });
  }

  handleCancel($event: MouseEvent) {
    if ($event) {
      this.mentionUsers = false;
      this.textareaReply.nativeElement.focus();
      this.cdRef.detectChanges();
    }
  }

  toggleMentionUsers($event: MouseEvent) {
    $event.stopPropagation();
    $event.preventDefault();
    this.mentionUsers = !this.mentionUsers;
  }

  updateTopicAndReturnToView(event: number) {
    if (event === -1) {
      this.updateOverview().then();
    } else {
      return new Promise(resolve => {
        this.refresh().then(() => {
          this.discussionService.selectedTopic = null;
          if (!this.discussionService?.discussions?.value) {
            return;
          }
          for (const item of this.discussionService.discussions.value) {
            if (item.id === event) {
              this.selectTopic(this.discussionService.selectedGroup, item, null).then(() => {
                resolve(true);
              });
            }
          }
        });
      });
    }
  }

  checkForDiscussions(): boolean {
    let result: boolean = false;
    for (const group of this.discussionService.groupedDiscussionList) {
      if (group.topics.length > 0) {
        result = true;
      }
    }
    return result;
  }

  checkViewToReturnTo($event: MouseEvent) {
    if ($event) {
      $event.stopPropagation();
    }
    if (this.discussionService.inputChanged) {
      this.discussionService.inputChangedModal().then((result: boolean) => {
        if (result) {
          this.discussionService.closeNewTopic();
          this.viewToReturnTo();
        }
      });
    } else {
      this.viewToReturnTo();
    }
  }

  closeTopic(): Promise<boolean> {
    return new Promise(resolve => {
      this.refresh().then(() => {
        this.discussionService.selectedTopic = null;
        resolve(true);
      });
    });
  }

  updateTopic(): Promise<boolean> {
    return new Promise(resolve => {
      const tempObj = this.discussionService.selectedTopic;
      this.closeTopic().then(() => {
        this.selectTopic(this.discussionService.selectedGroup, tempObj, null).then(() => {
          resolve(true);
        });
      });
    });
  }

  updateOverview() {
    return new Promise(resolve => {
      this.discussionService.selectedGroup = null;
      this.refresh().then(() => {
        this.discussionService.selectedTopic = null;
        resolve(true);
      });
    });
  }

  refresh(): Promise<boolean> {
    return new Promise(resolve => {
      this.discussionService.refreshDiscussionTopics(false).then(() => {
        resolve(true);
      });
    });
  }

  getCurrentTime(): SbDateTime {
    return new SbDateTime(DateTime.local());
  }

  getTraversedArray(levelFourArray: PostFullDTO[]): PostFullDTO[] {
    this.fifthLevel = [];
    if (!levelFourArray) {
      return [];
    }
    // decide whether pre or post order traversing // this.postOrderTraverse(levelFourArray);
    this.preOrderTraverse(levelFourArray);
    return this.fifthLevel;
  }

  postOrderTraverse(posts: PostFullDTO[]) {
    for (const item of posts) {
      if (item.answers) {
        this.postOrderTraverse(item.answers);
      }
      this.fifthLevel.push(item);
    }
  }

  preOrderTraverse(posts: PostFullDTO[]) {
    for (const item of posts) {
      this.fifthLevel.push(item);
      if (item.answers) {
        this.postOrderTraverse(item.answers);
      }
    }
  }

  private viewToReturnTo() {
    this.discussionService.newTopic = false;
    if (!this.discussionService.cameFromSubTopic) {
      this.updateOverview().then();
    } else if (this.discussionService.cameFromSubTopicMemory) {
      if (this.discussionService.selectedTopic) {
        this.closeTopic().then(() => {
          this.discussionService.selectDiscussableGroup(this.discussionService.selectedGroup.element.id);
          this.discussionService.cameFromSubTopicMemory = false;
        });
      } else {
        this.updateOverview().then(() => {
          this.discussionService.cameFromSubTopic = false;
        });
      }
    } else {
      this.updateOverview().then(() => {
        this.discussionService.cameFromSubTopic = false;
      });
    }
  }

  removeUser(user: UserDTO) {
    if (user && this.mentions?.length > 0) {
      const index = this.mentions.indexOf(user);
      if (index !== -1) {
        this.mentions.splice(index, 1);
      }
    }
  }
}
